import * as v from "@valibot/valibot";

// ============================================================
// API Schemas
// ============================================================

import { Address, Hex, UnsignedInteger } from "../../_schemas.ts";

/**
 * Link staking and trading accounts for fee discount attribution.
 * @see https://hyperliquid.gitbook.io/hyperliquid-docs/trading/fees#staking-linking
 */
export const LinkStakingUserRequest = /* @__PURE__ */ (() => {
  return v.object({
    /** Action to perform. */
    action: v.object({
      /** Type of action. */
      type: v.literal("linkStakingUser"),
      /** Chain ID in hex format for EIP-712 signing. */
      signatureChainId: Hex,
      /** Hyperliquid network type. */
      hyperliquidChain: v.picklist(["Mainnet", "Testnet"]),
      /**
       * Target account address.
       * - Trading user initiating: enter staking account address.
       * - Staking user finalizing: enter trading account address.
       */
      user: Address,
      /**
       * Link phase.
       * - `false` = trading user initiates link request.
       * - `true` = staking user finalizes permanent link.
       */
      isFinalize: v.boolean(),
      /** Nonce (timestamp in ms) used to prevent replay attacks. */
      nonce: UnsignedInteger,
    }),
    /** Nonce (timestamp in ms) used to prevent replay attacks. */
    nonce: UnsignedInteger,
    /** ECDSA signature components. */
    signature: v.object({
      /** First 32-byte component. */
      r: v.pipe(Hex, v.length(66)),
      /** Second 32-byte component. */
      s: v.pipe(Hex, v.length(66)),
      /** Recovery identifier. */
      v: v.picklist([27, 28]),
    }),
  });
})();
export type LinkStakingUserRequest = v.InferOutput<typeof LinkStakingUserRequest>;

/**
 * Successful response without specific data or error response.
 * @see https://hyperliquid.gitbook.io/hyperliquid-docs/trading/fees#staking-linking
 */
export type LinkStakingUserResponse =
  | {
    /** Successful status. */
    status: "ok";
    /** Response details. */
    response: {
      /** Type of response. */
      type: "default";
    };
  }
  | {
    /** Error status. */
    status: "err";
    /** Error message. */
    response: string;
  };

// ============================================================
// Execution Logic
// ============================================================

import { parse } from "../../../_base.ts";
import { canonicalize } from "../../../signing/mod.ts";
import {
  type ExchangeConfig,
  type ExcludeErrorResponse,
  executeUserSignedAction,
  type ExtractRequestOptions,
} from "./_base/mod.ts";

/** Schema for action fields (excludes request-level system fields). */
const LinkStakingUserActionSchema = /* @__PURE__ */ (() => {
  return v.omit(
    v.object(LinkStakingUserRequest.entries.action.entries),
    ["signatureChainId", "hyperliquidChain", "nonce"],
  );
})();

/** Action parameters for the {@linkcode linkStakingUser} function. */
export type LinkStakingUserParameters = Omit<v.InferInput<typeof LinkStakingUserActionSchema>, "type">;

/** Request options for the {@linkcode linkStakingUser} function. */
export type LinkStakingUserOptions = ExtractRequestOptions<v.InferInput<typeof LinkStakingUserRequest>>;

/** Successful variant of {@linkcode LinkStakingUserResponse} without errors. */
export type LinkStakingUserSuccessResponse = ExcludeErrorResponse<LinkStakingUserResponse>;

/** EIP-712 types for the {@linkcode linkStakingUser} function. */
export const LinkStakingUserTypes = {
  "HyperliquidTransaction:LinkStakingUser": [
    { name: "hyperliquidChain", type: "string" },
    { name: "user", type: "address" },
    { name: "isFinalize", type: "bool" },
    { name: "nonce", type: "uint64" },
  ],
};

/**
 * Link staking and trading accounts for fee discount attribution.
 *
 * Signing: User-Signed EIP-712.
 *
 * @param config General configuration for Exchange API requests.
 * @param params Parameters specific to the API request.
 * @param opts Request execution options.
 * @return Successful response without specific data.
 *
 * @throws {ValidationError} When the request parameters fail validation (before sending).
 * @throws {TransportError} When the transport layer throws an error.
 * @throws {ApiRequestError} When the API returns an unsuccessful response.
 *
 * @example
 * ```ts
 * import { HttpTransport } from "@nktkas/hyperliquid";
 * import { linkStakingUser } from "@nktkas/hyperliquid/api/exchange";
 * import { privateKeyToAccount } from "npm:viem/accounts";
 *
 * const wallet = privateKeyToAccount("0x..."); // viem or ethers
 * const transport = new HttpTransport(); // or `WebSocketTransport`
 *
 * await linkStakingUser({ transport, wallet }, {
 *   user: "0x...",
 *   isFinalize: false,
 * });
 * ```
 *
 * @see https://hyperliquid.gitbook.io/hyperliquid-docs/trading/fees#staking-linking
 */
export function linkStakingUser(
  config: ExchangeConfig,
  params: LinkStakingUserParameters,
  opts?: LinkStakingUserOptions,
): Promise<LinkStakingUserSuccessResponse> {
  const action = canonicalize(
    LinkStakingUserActionSchema,
    parse(LinkStakingUserActionSchema, { type: "linkStakingUser", ...params }),
  );
  return executeUserSignedAction(config, action, LinkStakingUserTypes, opts);
}
